/**
 * Searchable list of the GitHub repos Ceetrix can see
 * Story 226: Projects Dashboard (as RepoBrowserModal)
 * Story 585: inline list inside RepoAccessResolver; picking only reports the
 * repo, so the access check in RepoAccessResolver decides what happens next
 */

import { useState, useMemo, useEffect } from 'react';
import type { GitHubRepoListItem } from '../../hooks/useGitHubRepos';
import './RepoPicker.css';

const SEARCH_DEBOUNCE_MS = 300;

interface RepoPickerProps {
  repos: GitHubRepoListItem[];
  /** Called with the repo's full name (owner/name) when a row is clicked */
  onPick: (fullName: string) => void;
}

export function RepoPicker({ repos, onPick }: RepoPickerProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Filter repos by search query
  const filteredRepos = useMemo(() => {
    if (!debouncedQuery.trim()) return repos;

    const query = debouncedQuery.trim().toLowerCase();
    return repos.filter(
      (repo) =>
        repo.full_name.toLowerCase().includes(query) ||
        repo.name.toLowerCase().includes(query) ||
        repo.owner.toLowerCase().includes(query)
    );
  }, [repos, debouncedQuery]);

  return (
    <div className="repo-picker" data-testid="repo-picker">
      <input
        type="text"
        className="repo-picker__search-input"
        placeholder="Search your repositories..."
        aria-label="Search your repositories"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        autoFocus
        data-testid="repo-search-input"
      />

      {filteredRepos.length === 0 ? (
        <div className="repo-picker__empty">No repositories match your search.</div>
      ) : (
        <ul className="repo-picker__list" aria-label="Your repositories">
          {filteredRepos.map((repo) => (
            <li key={repo.full_name} className="repo-picker__item">
              <button
                className="repo-picker__repo"
                onClick={() => onPick(repo.full_name)}
                type="button"
                data-testid="repo-option"
              >
                <span className="repo-picker__repo-name">{repo.name}</span>
                <span className="repo-picker__repo-owner">{repo.owner}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
